import { Color, SvgGraphics, Container, ColorFill } from '@tencent/alloy-renderer';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { CANVAS_TABLE_BORDER_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';
import { StrokeType } from '@tencent/alloy-graphics';

interface IBorderInfo {
    key: string,
    left: number,
    top: number,
    width: number,
    height: number,
    backgroundColor: string,
    style?: string,
}

export const TableButtStrokeDash = {
    [StrokeType.SOLID]: '',
    [StrokeType.SYS_DOT]: '1 1',
    [StrokeType.SYS_DASH]: '3 1',
    [StrokeType.DASH]: '4 3',
    [StrokeType.DASH_DOT]: '4 3 1 3',
    [StrokeType.LG_DASH]: '7 3',
    [StrokeType.LG_DASH_DOT]: '7 3 1 3',
    [StrokeType.LG_DASH_DOT_DOT]: '7 3 1 3 1 3',
    [StrokeType.DOT]: '1 3',
    [StrokeType.SYS_DASH_DOT]: '3 1 1 1',
    [StrokeType.SYS_DASH_DOT_DOT]: '3 1 1 1 1 1',
};

interface IBindBorderInfo extends IBorderInfo {
    isBinding?: boolean;
}

export class TableBorderComponent extends Container {
    private model: Record<string, IBorderInfo>;
    private readonly factoryOptions: InterfaceFactoryOptions;

    public constructor(factoryOptions: InterfaceFactoryOptions) {
        super();
        this.factoryOptions = factoryOptions;
    }

    public updateModel(model: Record<string, IBorderInfo>): void {
        this.model = model;
        this.updateComponent();
    }

    private updateComponent(): void {
        this.children.slice().forEach(child => this.removeChild(child));
        this.drawBorders(this.getBorderGroupMap());
    }

    /**
     * 通过border的粗细和颜色进行分组，同一组border聚合成一个component
     * @private
     */
    private getBorderGroupMap() {
        const map = new Map();
        Object.values(this.model).forEach((border) => {
            const { key, backgroundColor, height, width, style } = border;
            if (!key || (!key.startsWith('h_border_') && !key.startsWith('v_border_')) || !backgroundColor) {
                return;
            }
            const strokeWidth = key.startsWith('h_border_') ? height : width;
            let groupKey = `${strokeWidth}$$$${backgroundColor}`;
            if (style) {
                groupKey = `${groupKey}$$$${style}`;
            }
            if (!map.has(groupKey)) {
                map.set(groupKey, []);
            }
            const borderGroupItem = map.get(groupKey);
            borderGroupItem.push(border);
        });
        return map;
    }

    /**
     * 合并连续的边框到同一个path的M L下，避免绘制不连续的虚线
     * @private
     */
    private mergeContinuousBorder(borderGroupMap) {
        borderGroupMap.forEach((borderArr, key) => {
            borderArr.forEach((item: IBindBorderInfo) => {
                const { isBinding, key } = item;
                if (key.startsWith('h_border_') && !isBinding) {
                    this.bindHoriContinuousBorder(item, borderArr);
                } else if (key.startsWith('v_border_') && !isBinding) {
                    this.bindVertContinuousBorder(item, borderArr);
                }
            });

            const newBorderArr = borderArr.filter((item: IBindBorderInfo) => !item.isBinding);

            borderGroupMap.set(key, newBorderArr);
        });
    }

    /**
     * 合并水平边框
     * @param item
     * @param borderArr
     * @private
     */
    private bindHoriContinuousBorder(item: IBindBorderInfo, borderArr: Array<IBindBorderInfo>) {
        for (let index = 0; index < borderArr.length; index++) {
            const { left, width, top } = item;
            const bindItem = borderArr[index];
            const { key, left: bindLeft, width: bindWidth, isBinding, top: bindTop } = bindItem;
            if (key.startsWith('h_border_')
                && (left + width) === bindLeft
                && !isBinding
                && top === bindTop
            ) {
                bindItem.isBinding = true;
                item.width = width + bindWidth;
            }
        }
    }

    /**
     * 合并垂直边框
     * @param item
     * @param borderArr
     * @private
     */
    private bindVertContinuousBorder(item: IBindBorderInfo, borderArr: Array<IBindBorderInfo>) {
        for (let index = 0; index < borderArr.length; index++) {
            const { top, height, left } = item;
            const bindItem = borderArr[index];
            const { key, top: bindTop, height: bindHeight, isBinding, left: bindLeft } = bindItem;
            if (key.startsWith('v_border_')
                && (top + height) === bindTop
                && !isBinding
                && left === bindLeft
            ) {
                bindItem.isBinding = true;
                item.height = height + bindHeight;
            }
        }
    }

    /**
     * 绘制border
     * @param borderGroupMap
     * @private
     */
    private drawBorders(borderGroupMap) {
        this.mergeContinuousBorder(borderGroupMap);
        const graphicsList: SvgGraphics[] = [];
        borderGroupMap.forEach((borderArr, key) => {
            const [strokeWidth, stroke, type] = key.split('$$$');
            const strokeWidthNumber = Number(strokeWidth);
            let strokeOffset = 0;
            if (strokeWidthNumber !== 2) {
                strokeOffset = strokeWidth / 2;
            }
            const paths = borderArr.map((item) => {
                let str;
                if (item.key.startsWith('h_border_')) {
                    str = `M ${item.left} ${item.top} L ${item.left + item.width} ${item.top}`;
                } else {
                    str = `M ${item.left} ${item.top - strokeOffset} L ${item.left} ${item.top + item.height + strokeOffset}`;
                }
                return str;
            }).join(' ');
            const graphics = new SvgGraphics(paths);
            graphics.strokeWidth = strokeWidth;
            graphics.stroke = new ColorFill(Color.fromHex(stroke));
            if (type) {
                const dashArr = this.getDashArray(type, strokeWidth);
                graphics.strokeDash = dashArr;
            }
            graphics.id = `${CANVAS_TABLE_BORDER_ID_PREFIX}-${key}`;
            graphicsList.push(graphics);
        });

        // 单元格绘制顺序按宽度排序
        graphicsList.sort((a, b) => a.strokeWidth - b.strokeWidth);
        graphicsList.forEach((graphics) => {
            this.addChild(graphics);
        });
    }

    private getDashArray(key: string, strokeWidth: number) {
        const dot = TableButtStrokeDash[key];
        return dot.split(' ').map(item => Number(item) * strokeWidth) || [];
    }
}
